// src/screens/settings/MasterDataListPage.jsx
import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  GripVertical,
  PlusCircle,
  X,
  PlusSquare,
  ChevronDown,
  ChevronUp,
  FileText,
} from "lucide-react";
import { ReactComponent as Trash } from "../../assets/images/Trash.svg";
import { ReactComponent as Pen } from "../../assets/images/Pen.svg";
import { createHttpClient } from "../../api/client";
import { useNavigate } from "react-router-dom";

function cx(...xs) {
  return xs.filter(Boolean).join(" ");
}

function moveItem(arr, from, to) {
  const next = [...arr];
  const [picked] = next.splice(from, 1);
  next.splice(to, 0, picked);
  return next;
}

function ConfirmModal({
  open,
  title = "ยืนยันการทำรายการ",
  description = "",
  confirmText = "ยืนยัน",
  cancelText = "ยกเลิก",
  danger = false,
  onConfirm,
  onClose,
}) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      <div
        className="absolute inset-0 bg-slate-900/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-lg font-extrabold text-slate-900 truncate">
                {title}
              </div>
              {description ? (
                <div className="mt-1 text-sm text-slate-500">{description}</div>
              ) : null}
            </div>

            <button
              type="button"
              onClick={onClose}
              className="h-9 w-9 rounded-xl border border-slate-200 bg-white inline-flex items-center justify-center hover:bg-slate-50 active:scale-[0.98]"
              aria-label="close"
              title="ปิด"
            >
              <X size={16} />
            </button>
          </div>

          <div className="px-5 py-4 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl px-4 py-2 text-sm font-bold border border-slate-200 bg-white hover:bg-slate-50 active:scale-[0.99]"
            >
              {cancelText}
            </button>

            <button
              type="button"
              onClick={onConfirm}
              className={cx(
                "rounded-xl px-4 py-2 text-sm font-bold text-white active:scale-[0.99]",
                danger
                  ? "bg-[#FF1607] hover:opacity-95"
                  : "bg-[#111111] hover:opacity-95"
              )}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditModal({
  open,
  title = "แก้ไขรายการ",
  label = "ชื่อรายการ",
  value = "",
  confirmText = "บันทึก",
  cancelText = "ยกเลิก",
  loading = false,
  onClose,
  onConfirm,
}) {
  const [text, setText] = useState(value || "");

  useEffect(() => {
    if (!open) return;
    setText(value || "");
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90]">
      <div
        className="absolute inset-0 bg-slate-900/40"
        onClick={loading ? undefined : onClose}
        aria-hidden="true"
      />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-lg font-extrabold text-slate-900 truncate">
                {title}
              </div>
            </div>

            <button
              type="button"
              onClick={loading ? undefined : onClose}
              className="h-9 w-9 rounded-xl border border-slate-200 bg-white inline-flex items-center justify-center hover:bg-slate-50 active:scale-[0.98]"
              aria-label="close"
              title="ปิด"
              disabled={loading}
            >
              <X size={16} />
            </button>
          </div>

          <div className="px-5 py-5">
            <div className="text-sm font-semibold text-slate-700 mb-2">
              {label}
            </div>
            <input
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-slate-400"
              placeholder="กรอกข้อมูล"
              disabled={loading}
            />
          </div>

          <div className="px-5 pb-5 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="rounded-xl px-4 py-2 text-sm font-bold border border-slate-200 bg-white hover:bg-slate-50 active:scale-[0.99] disabled:opacity-60"
            >
              {cancelText}
            </button>

            <button
              type="button"
              onClick={() => onConfirm?.(text)}
              disabled={loading || !String(text).trim()}
              className="rounded-xl px-4 py-2 text-sm font-bold text-white active:scale-[0.99] bg-[#111111] hover:opacity-95 disabled:opacity-60"
            >
              {loading ? "กำลังบันทึก..." : confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TemplateModal({
  open,
  title = "เทมเพลตคำอธิบายสินค้า",
  value = "",
  loading = false,
  onClose,
  onSave,
}) {
  const [text, setText] = useState(value || "");

  useEffect(() => {
    if (!open) return;
    setText(value || "");
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[95]">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={loading ? undefined : onClose}
      />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-5xl rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
          <div className="px-8 py-6 border-b border-slate-200 flex items-start justify-between gap-3">
            <div className="text-2xl font-extrabold text-slate-900">
              {title}
            </div>

            <button
              type="button"
              onClick={loading ? undefined : onClose}
              className="h-10 w-10 rounded-xl bg-white inline-flex items-center justify-center hover:bg-slate-50"
              disabled={loading}
            >
              <X size={22} />
            </button>
          </div>

          <div className="px-8 py-6">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={18}
              disabled={loading}
              className="w-full rounded-xl border border-slate-300 bg-white p-4 text-lg leading-8 outline-none resize-none"
              placeholder="กรอกเทมเพลต..."
            />
          </div>

          <div className="px-8 pb-8 grid grid-cols-2 gap-6">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="h-14 rounded-2xl border border-slate-300 bg-white text-xl font-bold hover:bg-slate-50 disabled:opacity-60"
            >
              ยกเลิก
            </button>

            <button
              type="button"
              onClick={() => onSave?.(text)}
              disabled={loading}
              className="h-14 rounded-2xl bg-[#111111] text-white text-xl font-bold hover:opacity-95 disabled:opacity-60"
            >
              {loading ? "กำลังบันทึก..." : "บันทึก"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function mapApiToItems(apiData = [], fieldKey = "name") {
  return (apiData || []).map((x) => ({
    ...x,
    id: String(x?.id),
    [fieldKey]: x?.[fieldKey] ?? "",
  }));
}

export default function MasterDataListPage({
  title = "Master Data",
  subtitle = "",
  initialItems = [],
  apiPath,
  fieldKey = "name",
  createDefaultName = "รายการใหม่",

  parentId,
  queryParamsBuilder,
  createPayloadBuilder,
  updatePayloadBuilder,
  onAfterCreate,

  showSubButton = false,
  subLabel = "เพิ่มรายการย่อย",
  subRouteBuilder,
  onAddSub,

  showSubDropdown = false,
  subTitle = "รายการย่อย",
  subFieldKey = "name",
  subListFetcher,

  showTemplateButton = false,
  templateButtonTitle = "แก้ไขเทมเพลต",
  templateValue = "",
  onSaveTemplate,
}) {
  const http = useMemo(() => createHttpClient(), []);
  const nav = useNavigate();
  const headerTitle = useMemo(() => title, [title]);

  const normalizedInitialItems = useMemo(() => {
    return (initialItems || []).map((it) => ({
      id: String(it?.id ?? crypto.randomUUID()),
      [fieldKey]: it?.[fieldKey] ?? it?.name ?? it?.size ?? "",
    }));
  }, [initialItems, fieldKey]);

  const [items, setItems] = useState(normalizedInitialItems);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [draggingIndex, setDraggingIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);

  const containerRef = useRef(null);
  const dragIndexRef = useRef(null);
  const pointerIdRef = useRef(null);
  const isDraggingRef = useRef(false);

  const [expandedId, setExpandedId] = useState(null);
  const [subMap, setSubMap] = useState({});

  const getLabel = (it) => String(it?.[fieldKey] ?? "");
  const getSubLabel = (x) => String(x?.[subFieldKey] ?? x?.name ?? "");

  useEffect(() => {
    if (!apiPath) return;

    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const params =
          typeof queryParamsBuilder === "function"
            ? queryParamsBuilder(parentId)
            : undefined;

        const res = await http.get(apiPath, params ? { params } : undefined);
        const list = mapApiToItems(res?.data?.data || [], fieldKey);

        if (!alive) return;
        setItems(list);
      } catch (e) {
        console.error("GET master error:", e);
        if (!alive) return;
        setError("โหลดข้อมูลไม่สำเร็จ");
        setItems(normalizedInitialItems);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [http, apiPath, fieldKey, normalizedInitialItems, parentId, queryParamsBuilder]);

  const resetDrag = () => {
    isDraggingRef.current = false;
    dragIndexRef.current = null;
    pointerIdRef.current = null;
    setDraggingIndex(null);
    setOverIndex(null);
    document.body.classList.remove("select-none");
  };

  const getIndexFromPoint = (clientY) => {
    const root = containerRef.current;
    if (!root) return null;
    const els = root.querySelectorAll("[data-row='true']");
    for (let i = 0; i < els.length; i++) {
      const r = els[i].getBoundingClientRect();
      if (clientY >= r.top && clientY <= r.bottom)
        return Number(els[i].dataset.index);
    }
    return null;
  };

  const onPointerMove = (e) => {
    if (!isDraggingRef.current) return;
    if (pointerIdRef.current !== null && e.pointerId !== pointerIdRef.current)
      return;

    const over = getIndexFromPoint(e.clientY);
    if (over === null || over === undefined) return;

    setOverIndex(over);

    const from = dragIndexRef.current;
    if (from === null || from === undefined) return;
    if (from === over) return;

    setItems((prev) => {
      const next = moveItem(prev, from, over);
      dragIndexRef.current = over;
      setDraggingIndex(over);
      return next;
    });
  };

  const onPointerUp = (e) => {
    if (!isDraggingRef.current) return;
    if (pointerIdRef.current !== null && e.pointerId !== pointerIdRef.current)
      return;
    resetDrag();
  };

  useEffect(() => {
    window.addEventListener("pointermove", onPointerMove, { passive: false });
    window.addEventListener("pointerup", onPointerUp, { passive: false });
    window.addEventListener("pointercancel", onPointerUp, { passive: false });
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createItem = async () => {
    const defaultValue = createDefaultName;

    if (!apiPath) {
      setItems((prev) => [
        { id: crypto.randomUUID(), [fieldKey]: defaultValue },
        ...prev,
      ]);
      return;
    }

    setError("");
    try {
      const payload =
        typeof createPayloadBuilder === "function"
          ? createPayloadBuilder(defaultValue, parentId)
          : { data: { [fieldKey]: defaultValue } };

      const res = await http.post(apiPath, payload);

      const created = res?.data?.data;
      const newId = created?.id ?? created?.data?.id;
      const newValue =
        created?.[fieldKey] ?? created?.data?.[fieldKey] ?? defaultValue;

      if (!newId) throw new Error("missing id from create");

      const newItem = { id: String(newId), [fieldKey]: String(newValue ?? "") };
      setItems((prev) => [newItem, ...prev]);

      if (typeof onAfterCreate === "function") {
        try {
          await onAfterCreate(created, parentId);
        } catch (e) {
          console.error("onAfterCreate error:", e);
        }
      }
    } catch (e) {
      console.error("POST master error:", e);
      setError("เพิ่มรายการไม่สำเร็จ");
    }
  };

  const askEdit = (it) => {
    setPendingEdit(it);
    setEditOpen(true);
  };

  const confirmEdit = async (nextValue) => {
    const trimmed = String(nextValue ?? "").trim();
    if (!trimmed || !pendingEdit) return;

    const it = pendingEdit;

    if (!apiPath) {
      setItems((prev) =>
        prev.map((x) => (x.id === it.id ? { ...x, [fieldKey]: trimmed } : x))
      );
      setEditOpen(false);
      setPendingEdit(null);
      return;
    }

    const prevItems = items;
    setEditSaving(true);
    setItems((xs) =>
      xs.map((x) => (x.id === it.id ? { ...x, [fieldKey]: trimmed } : x))
    );

    try {
      const payload =
        typeof updatePayloadBuilder === "function"
          ? updatePayloadBuilder(trimmed, it, parentId)
          : { data: { [fieldKey]: trimmed } };
      await http.put(`${apiPath}/${it.id}`, payload);
      setEditOpen(false);
      setPendingEdit(null);
    } catch (e) {
      console.error("PUT master error:", e);
      setError("แก้ไขไม่สำเร็จ");
      setItems(prevItems);
    } finally {
      setEditSaving(false);
    }
  };

  const closeEdit = () => {
    if (editSaving) return;
    setEditOpen(false);
    setPendingEdit(null);
  };

  const handleSaveTemplate = async (nextValue) => {
    try {
      setTemplateSaving(true);
      await onSaveTemplate?.(nextValue);
      setTemplateOpen(false);
    } catch (e) {
      console.error("SAVE template error:", e);
      setError("บันทึกเทมเพลตไม่สำเร็จ");
    } finally {
      setTemplateSaving(false);
    }
  };

  const askDelete = (it) => {
    setPendingDelete({ id: it.id, label: getLabel(it) });
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!pendingDelete?.id) return;

    const deletingId = pendingDelete.id;
    const prevItems = items;

    setConfirmOpen(false);
    setPendingDelete(null);

    if (!apiPath) {
      setItems((prev) => prev.filter((x) => x.id !== deletingId));
      return;
    }

    setItems((xs) => xs.filter((x) => x.id !== deletingId));

    try {
      await http.delete(`${apiPath}/${deletingId}`);
    } catch (e) {
      console.error("DELETE master error:", e);
      setError("ลบรายการไม่สำเร็จ");
      setItems(prevItems);
    }
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setPendingDelete(null);
  };

  const handleAddSub = (it) => {
    if (typeof onAddSub === "function") return onAddSub(it);
    if (typeof subRouteBuilder === "function") {
      const path = subRouteBuilder(it);
      if (path) nav(path, { state: { parent: it } });
    }
  };

  const toggleSub = async (it) => {
    if (!showSubDropdown) return;

    const id = String(it.id);

    if (expandedId === id) {
      setExpandedId(null);
      return;
    }

    setExpandedId(id);

    if (subMap?.[id]?.items?.length || subMap?.[id]?.loaded) return;

    if (typeof subListFetcher !== "function") {
      setSubMap((m) => ({
        ...m,
        [id]: {
          loaded: true,
          loading: false,
          error: "ยังไม่ได้กำหนด subListFetcher",
          items: [],
        },
      }));
      return;
    }

    setSubMap((m) => ({
      ...m,
      [id]: { loaded: false, loading: true, error: "", items: [] },
    }));

    try {
      const list = await subListFetcher(it);
      setSubMap((m) => ({
        ...m,
        [id]: { loaded: true, loading: false, error: "", items: list || [] },
      }));
    } catch (e) {
      console.error("FETCH sub error:", e);
      setSubMap((m) => ({
        ...m,
        [id]: {
          loaded: true,
          loading: false,
          error: "โหลดรายการย่อยไม่สำเร็จ",
          items: [],
        },
      }));
    }
  };

  const navigateBack = useNavigate();

  return (
    <div className="p-5 md:p-8 min-h-full bg-white">
      <ConfirmModal
        open={confirmOpen}
        title="ยืนยันการลบรายการ"
        description={
          pendingDelete?.label
            ? `คุณต้องการลบ “${pendingDelete.label}” ใช่หรือไม่?`
            : "คุณต้องการลบรายการนี้ใช่หรือไม่?"
        }
        confirmText="ลบรายการ"
        cancelText="ยกเลิก"
        danger
        onConfirm={confirmDelete}
        onClose={closeConfirm}
      />

      <EditModal
        open={editOpen}
        title="แก้ไขรายการ"
        label="ชื่อรายการ"
        value={pendingEdit ? getLabel(pendingEdit) : ""}
        confirmText="บันทึก"
        cancelText="ยกเลิก"
        loading={editSaving}
        onClose={closeEdit}
        onConfirm={confirmEdit}
      />

      <TemplateModal
        open={templateOpen}
        title={templateButtonTitle}
        value={templateValue}
        loading={templateSaving}
        onClose={() => {
          if (templateSaving) return;
          setTemplateOpen(false);
        }}
        onSave={handleSaveTemplate}
      />

      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <span
              className="hover:underline cursor-pointer"
              onClick={() => navigateBack(-1)}
            >
              ตั้งค่า
            </span>
            {subtitle ? <span className="text-slate-300">/</span> : null}
            {subtitle ? (
              <span className="font-semibold text-[#047DED]">{subtitle}</span>
            ) : null}
          </div>
          <div className="mt-1 text-4xl font-extrabold tracking-tight">
            {headerTitle}
          </div>
        </div>
      </div>

      {error ? (
        <div className="mt-4 max-w-3xl rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <div className="mt-6 space-y-4 max-w-3xl" ref={containerRef}>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={createItem}
            className="inline-flex items-center gap-2 rounded-xl bg-[#111111] px-10 py-4 text-sm font-bold text-white shadow-md hover:opacity-95 active:scale-[0.99]"
          >
            <PlusCircle size={18} />
            เพิ่มรายการ
          </button>

          {/* {showTemplateButton && (
            <button
              type="button"
              onClick={() => setTemplateOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-4 text-sm font-bold text-slate-800 shadow-sm hover:bg-slate-50 active:scale-[0.99]"
            >
              <FileText size={18} />
              แก้ไข Template
            </button>
          )} */}
        </div>

        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-slate-500">
            กำลังโหลดข้อมูล...
          </div>
        ) : null}

        {!loading &&
          items.map((it, index) => {
            const isDragging = draggingIndex === index;
            const isOver = overIndex === index && draggingIndex !== index;

            const isExpanded = expandedId === String(it.id);
            const subState = subMap?.[String(it.id)];
            const subLoading = !!subState?.loading;
            const subError = subState?.error;
            const subItems = subState?.items || [];

            return (
              <div key={it.id} className="space-y-2">
                <div
                  data-row="true"
                  data-index={index}
                  className={cx(
                    "relative flex items-center justify-between gap-3 rounded-2xl bg-white px-4 py-4 shadow-sm transition",
                    "border",
                    isDragging
                      ? "border-blue-500 ring-2 ring-blue-200 bg-blue-50/40"
                      : isOver
                        ? "border-blue-300 ring-1 ring-blue-100"
                        : "border-slate-200"
                  )}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={cx(
                        "h-10 w-10 rounded-xl border flex items-center justify-center",
                        isDragging
                          ? "bg-blue-50 border-blue-200"
                          : "bg-slate-50 border-slate-200"
                      )}
                      title="ลากเพื่อเรียงลำดับ"
                      aria-label="drag handle"
                    >
                      <GripVertical className="text-slate-500" />
                    </div>

                    <div className="font-semibold truncate select-none">
                      {getLabel(it)}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {showSubButton && (
                      <button
                        type="button"
                        className="h-10 w-10 rounded-xl bg-[#F5F5F5] text-[#1D1616] inline-flex items-center justify-center hover:text-[#047DED] hover:bg-[#047DED0D]"
                        title={subLabel}
                        onClick={() => handleAddSub(it)}
                      >
                        <PlusSquare size={18} />
                      </button>
                    )}

                    <button
                      type="button"
                      className="h-10 w-10 rounded-xl bg-[#F5F5F5] text-[#1D1616] inline-flex items-center justify-center hover:text-[#FF1607] hover:bg-[#FFF3F3]"
                      title="ลบ"
                      onClick={() => askDelete(it)}
                    >
                      <Trash size={18} />
                    </button>

                    <button
                      type="button"
                      className="h-10 w-10 rounded-xl bg-[#F5F5F5] text-[#1D1616] inline-flex items-center justify-center hover:text-[#047DED] hover:bg-[#047DED0D]"
                      title="แก้ไข"
                      onClick={() => askEdit(it)}
                    >
                      <Pen size={18} />
                    </button>

                    {/* {showSubDropdown && (
                      <button
                        type="button"
                        className={cx(
                          "h-10 w-10 rounded-xl bg-[#F5F5F5] text-[#1D1616] inline-flex items-center justify-center",
                          "hover:text-[#111] hover:bg-[#EEEEEE]"
                        )}
                        title={isExpanded ? "หุบรายการย่อย" : "แสดงรายการย่อย"}
                        onClick={() => toggleSub(it)}
                      >
                        {isExpanded ? (
                          <ChevronUp size={18} />
                        ) : (
                          <ChevronDown size={18} />
                        )}
                      </button>
                    )} */}
                  </div>
                </div>

                {showSubDropdown && isExpanded && (
                  <div className="ml-12 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-extrabold text-slate-900">
                        {subTitle}
                      </div>
                      {subLoading ? (
                        <div className="text-xs text-slate-500">กำลังโหลด...</div>
                      ) : null}
                    </div>

                    {subError ? (
                      <div className="mt-2 text-sm text-rose-600">{subError}</div>
                    ) : null}

                    {!subLoading && !subError && subItems.length === 0 ? (
                      <div className="mt-2 text-sm text-slate-500">
                        ยังไม่มีรายการย่อย
                      </div>
                    ) : null}

                    {!subLoading && !subError && subItems.length > 0 ? (
                      <div className="mt-3 space-y-2">
                        {subItems.map((sx, i) => (
                          <div
                            key={sx?.id ?? i}
                            className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2"
                          >
                            <div className="text-sm font-semibold text-slate-800 truncate">
                              {getSubLabel(sx)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}

        {!loading && items.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-slate-500">
            ยังไม่มีรายการ
          </div>
        )}
      </div>
    </div>
  );
}